import * as Sentry from '@sentry/react';
import type React from 'react';
import { env, isDev, buildMode } from '@/core/config/env';
import type { ErrorReporter } from '@/core/error-boundaries/types';

const SCRUBBED_KEYS = ['password', 'token', 'secret', 'key', 'phone', 'address'];

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      if (SCRUBBED_KEYS.some((key) => k.toLowerCase().includes(key))) {
        return [k, '[Filtered]'];
      }
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        return [k, scrubObject(v as Record<string, unknown>)];
      }
      return [k, v];
    }),
  );
}

export function initSentry(): void {
  if (!env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: buildMode,
    integrations: [Sentry.browserTracingIntegration()],
    // 10% performance sampling in production; disabled elsewhere
    tracesSampleRate: buildMode === 'production' ? 0.1 : 0,

    beforeSend(event, hint) {
      // 401s are handled by authAdapter.logout() — not actionable in Sentry
      const err = hint?.originalException;
      if (err && typeof err === 'object' && 'response' in err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401) return null;
      }

      // Suppress all events in development to avoid noise
      if (isDev) return null;

      // Scrub sensitive request headers
      if (event.request?.headers) {
        event.request.headers = {
          ...event.request.headers,
          Authorization: '[Filtered]',
          Cookie: '[Filtered]',
        };
      }

      // Scrub entire request body — may contain user input
      if (event.request) {
        event.request.data = '[Filtered]';
      }

      // Keep only id from user object — never send email or username
      if (event.user) {
        event.user = { id: event.user.id };
      }

      // Scrub known PII key names from extra context
      if (event.extra) {
        event.extra = scrubObject(event.extra as Record<string, unknown>);
      }

      return event;
    },
  });
}

export function setSentryContext(userId: string, organizationId: string): void {
  if (!env.VITE_SENTRY_DSN) return;
  Sentry.setUser({ id: userId });
  Sentry.setTag('organizationId', organizationId);
}

export const sentryReporter: ErrorReporter = {
  report(error: Error, info: React.ErrorInfo): void {
    if (!env.VITE_SENTRY_DSN) return;
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    });
  },
};
